import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PaystackPop from "@paystack/inline-js";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import toast from "../../../helpers/toast";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import {
  usePurchaseParentAiCreditsMutation,
  useVerifyParentAiCreditsMutation,
  useGetParentAiBalanceQuery,
  useGetParentAiHistoryQuery,
  useUpdateParentAiSettingsMutation,
} from "../../../app/parentApiSlice";

// ─── Payment helpers ─────────────────────────────────────────────────────────

const EMPTY_FW_CONFIG = {
  public_key: "",
  tx_ref: "placeholder",
  amount: 0,
  currency: "NGN",
  customer: { email: "" },
  customizations: { title: "SchoolApp" },
};

function useInlinePayment() {
  const [fwConfig, setFwConfig] = useState(null);
  const pendingRef = useRef(null);
  const handleFlutterPayment = useFlutterwave(fwConfig || EMPTY_FW_CONFIG);

  useEffect(() => {
    if (fwConfig && pendingRef.current) {
      const onSuccess = pendingRef.current;
      handleFlutterPayment({
        callback: (response) => {
          closePaymentModal();
          pendingRef.current = null;
          setFwConfig(null);
          onSuccess(response.tx_ref);
        },
        onClose: () => {
          pendingRef.current = null;
          setFwConfig(null);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fwConfig]);

  const openInlinePayment = useCallback((result, onSuccess) => {
    const config = JSON.parse(JSON.stringify(result.config));
    const gatewayKey = result.gateway === "paystack" ? config.key : config.public_key;
    if (!gatewayKey) {
      const other = result.gateway === "paystack" ? "Flutterwave" : "Paystack";
      toast.error(`${result.gateway === "paystack" ? "Paystack" : "Flutterwave"} is not available. Please select ${other} instead.`);
      return;
    }
    if (result.gateway === "paystack") {
      const popup = new PaystackPop();
      popup.newTransaction({
        ...config,
        onSuccess: (tx) => onSuccess(tx.reference),
        onCancel: () => toast.info("Payment cancelled"),
      });
    } else {
      pendingRef.current = onSuccess;
      setFwConfig(config);
    }
  }, []);

  return openInlinePayment;
}

// ─── Balance card ─────────────────────────────────────────────────────────────

function BalanceCard({ balance, termUsed, limit, isPaused, threshold, onTopUp }) {
  const utilizationText =
    limit > 0
      ? `${termUsed} / ${limit} credits used this term`
      : `${termUsed} credits used this term`;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Credit Balance
          </Typography>
          <Typography variant="h3" fontWeight="bold" color="primary">
            {balance.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            credits &nbsp;·&nbsp; ₦{(balance * 10).toLocaleString()} equivalent
          </Typography>
        </Box>
        <Stack spacing={1} alignItems="flex-end">
          {isPaused && (
            <Chip
              icon={<PauseCircleOutlineIcon />}
              label="Access Paused"
              color="warning"
              size="small"
            />
          )}
          {balance <= threshold && balance > 0 && (
            <Chip label="Low balance" color="error" size="small" />
          )}
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={onTopUp}
          >
            Top Up
          </Button>
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        {utilizationText}
      </Typography>
    </Paper>
  );
}

// ─── Top-up dialog ────────────────────────────────────────────────────────────

function TopUpPanel({ onClose }) {
  const [credits, setCredits] = useState(100);
  const [gateway, setGateway] = useState("paystack");
  const [purchasing, setPurchasing] = useState(false);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState("");

  const [purchaseParentAiCredits] = usePurchaseParentAiCreditsMutation();
  const [verifyParentAiCredits] = useVerifyParentAiCreditsMutation();
  const openInlinePayment = useInlinePayment();

  const handleTopUp = async () => {
    if (credits < 50) {
      toast.error("Minimum top-up is 50 credits (₦500).");
      return;
    }
    if (needsEmail && !email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setPurchasing(true);
    try {
      const body = { credits, gateway };
      if (email.trim()) body.email = email.trim();
      const result = await purchaseParentAiCredits(body).unwrap();
      setNeedsEmail(false);
      openInlinePayment(result, async (reference) => {
        try {
          const verified = await verifyParentAiCredits({ reference, gateway }).unwrap();
          toast.success(`${verified.balance.toLocaleString()} credits now available.`);
          onClose();
        } catch {
          toast.error("Payment verification failed. Contact support if funds were deducted.");
        }
      });
    } catch (err) {
      if (err?.data?.error === "NO_EMAIL") {
        setNeedsEmail(true);
      } else {
        toast.error(err?.data?.message || "Could not initiate payment.");
      }
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Top Up Credits
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        1 credit = ₦10 &nbsp;·&nbsp; Minimum: 50 credits (₦500)
      </Typography>

      <Stack spacing={2} sx={{ mt: 2, maxWidth: 360 }}>
        <TextField
          label="Credits to purchase"
          type="number"
          value={credits}
          onChange={(e) => setCredits(Math.max(0, parseInt(e.target.value) || 0))}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="body2" color="text.secondary">
                  = ₦{(credits * 10).toLocaleString()}
                </Typography>
              </InputAdornment>
            ),
          }}
          size="small"
        />

        {needsEmail && (
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Required for payment processing"
            size="small"
            autoFocus
          />
        )}

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Payment gateway
          </Typography>
          <RadioGroup row value={gateway} onChange={(e) => setGateway(e.target.value)}>
            <FormControlLabel value="paystack" control={<Radio size="small" />} label="Paystack" />
            <FormControlLabel value="flutterwave" control={<Radio size="small" />} label="Flutterwave" />
          </RadioGroup>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={handleTopUp}
            disabled={purchasing || credits < 50}
          >
            {purchasing ? <CircularProgress size={20} /> : "Pay Now"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─── Settings panel ───────────────────────────────────────────────────────────

function SettingsPanel({ settings }) {
  const [limit, setLimit] = useState(settings?.spending_limit_per_term ?? 0);
  const [paused, setPaused] = useState(settings?.is_paused ?? false);
  const [threshold, setThreshold] = useState(settings?.low_balance_threshold ?? 20);
  const [saving, setSaving] = useState(false);

  const [updateSettings] = useUpdateParentAiSettingsMutation();

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        spending_limit_per_term: limit,
        is_paused: paused,
        low_balance_threshold: threshold,
      }).unwrap();
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Settings
      </Typography>

      <Stack spacing={2.5} sx={{ maxWidth: 420 }}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={paused}
                onChange={(e) => setPaused(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {paused ? (
                    <>
                      <PauseCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                      Access paused
                    </>
                  ) : (
                    <>
                      <PlayCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                      Access enabled
                    </>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When paused, your children cannot draw from this pool even if balance remains.
                </Typography>
              </Box>
            }
          />
        </Box>

        <Tooltip title="0 = unlimited. Your children collectively cannot exceed this many credits from your pool per school term.">
          <TextField
            label="Per-term spending limit (credits)"
            type="number"
            value={limit}
            onChange={(e) => setLimit(Math.max(0, parseInt(e.target.value) || 0))}
            helperText={limit === 0 ? "Unlimited — no cap applied" : `₦${(limit * 10).toLocaleString()} equivalent per term`}
            size="small"
            fullWidth
          />
        </Tooltip>

        <Tooltip title="You will receive an email alert when your balance drops to this level.">
          <TextField
            label="Low balance alert threshold (credits)"
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(Math.max(0, parseInt(e.target.value) || 0))}
            helperText={`Alert when balance ≤ ${threshold} credits (₦${threshold * 10})`}
            size="small"
            fullWidth
          />
        </Tooltip>

        <Box>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Save Settings"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

// ─── History table ─────────────────────────────────────────────────────────────

function HistoryTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isLoading } = useGetParentAiHistoryQuery({
    page: page + 1,
    per_page: rowsPerPage,
  });

  const txns = data?.transactions || [];
  const total = data?.total || 0;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!txns.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        No transactions yet.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell align="right">Credits</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {txns.map((tx) => (
              <StyledTableRow key={tx.id}>
                <StyledTableCell>
                  {new Date(tx.created_at).toLocaleDateString("en-NG", {
                    day: "numeric", month: "short", year: "numeric",
                  })}
                </StyledTableCell>
                <StyledTableCell sx={{ maxWidth: 300 }}>
                  <Typography variant="body2" noWrap>
                    {tx.description || "—"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={tx.amount > 0 ? "success.main" : "error.main"}
                  >
                    {tx.amount > 0 ? "+" : ""}
                    {tx.amount}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
      />
    </>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function ParentAICredits() {
  const [showTopUp, setShowTopUp] = useState(false);

  const { data, isLoading, error } = useGetParentAiBalanceQuery();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load Credits information.
      </Alert>
    );
  }

  return (
    <Box>
      <BalanceCard
        balance={data?.balance ?? 0}
        termUsed={data?.term_credits_used ?? 0}
        limit={data?.spending_limit_per_term ?? 0}
        isPaused={data?.is_paused ?? false}
        threshold={data?.low_balance_threshold ?? 20}
        onTopUp={() => setShowTopUp(true)}
      />

      {showTopUp && <TopUpPanel onClose={() => setShowTopUp(false)} />}

      <SettingsPanel
        settings={{
          spending_limit_per_term: data?.spending_limit_per_term,
          is_paused: data?.is_paused,
          low_balance_threshold: data?.low_balance_threshold,
        }}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Transaction History
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Credit purchases and deductions from your children's AI quiz sessions.
      </Typography>

      <HistoryTable />
    </Box>
  );
}
